/**
 * Which service you enlisted in, and how far up it you can get.
 *
 * Career mode has exactly **two** playable nations — *"You will enlist in
 * either the Royal Flying Corps or the German Air Service as a fighter pilot"*
 * (manual, "Career Play") — and `PS.EXE` carries all three service strings
 * because the RFC becomes the **Royal Air Force** on 1 April 1918 rather than
 * because there is a third choice at enlistment (docs/game/campaign.md
 * § *Starting a career*). `serviceFor` is that rename, applied by the calendar.
 *
 * The **ladder is three rungs per side**, not the four the raw string dump
 * implies: the manual lists *"Second Lieutenant → Leutnant / First Lieutenant →
 * Oberleutnant / Captain → Rittmeister or Hauptmann"*, with Rittmeister and
 * Hauptmann as alternate titles for one rank rather than two rungs
 * (docs/game/campaign.md § *Ranks and promotion*). Titles and abbreviations
 * below are verbatim from that document.
 *
 * **Promotion is scored, not counted.** The manual is explicit twice — *"As
 * your score increases, you will be promoted in rank"* and *"In Career mode,
 * your score has a great impact upon your ability to advance in rank"* — so the
 * gate is career score, the running total of every mission score plus the value
 * of every medal (manual, "View Pilot Record"). The two threshold numbers are
 * **port-side**: nothing decoded gives them, and they are set so a competent
 * pilot leads a flight after a handful of successful sorties and reaches
 * Captain after a tour's worth.
 */

import type { Nation } from "../mission/mission-roster.ts";
import { RAF_FORMED_DATE, isOnOrAfter, type CareerDate } from "./calendar.ts";

export const SERVICE_BRANCHES = ["rfc", "raf", "gas"] as const;

export type ServiceBranch = (typeof SERVICE_BRANCHES)[number];

/** Verbatim `PS.EXE` service names. */
export const SERVICE_LABELS: Readonly<Record<ServiceBranch, string>> = {
  rfc: "Royal Flying Corps",
  raf: "Royal Air Force",
  gas: "German Air Service",
};

/** The branch a pilot of this nation is serving in on this date. */
export function serviceFor(nation: Nation, date: CareerDate): ServiceBranch {
  if (nation === "german") return "gas";
  return isOnOrAfter(date, RAF_FORMED_DATE) ? "raf" : "rfc";
}

export interface Rank {
  readonly title: string;
  /** `PS.EXE`'s own short form, for the pilot sheet's header line. */
  readonly abbreviation: string;
  /** What this rung unlocks, in the manual's own terms. */
  readonly privilege: string;
}

function rank(title: string, abbreviation: string, privilege: string): Rank {
  return { title, abbreviation, privilege };
}

const WINGMAN_PRIVILEGE = "Wingman — you fly where the flight leader flies.";
const LEADER_PRIVILEGE = "Flight leader — a flight of two, yours to lead.";
const COMMAND_PRIVILEGE = "Command of flights of up to four, and a personal aircraft of your own.";

export const CAREER_RANKS: Readonly<Record<Nation, readonly Rank[]>> = {
  allied: [
    rank("Second Lieutenant", "2nd Lt", WINGMAN_PRIVILEGE),
    rank("First Lieutenant", "1st Lt", LEADER_PRIVILEGE),
    rank("Captain", "Capt", COMMAND_PRIVILEGE),
  ],
  german: [
    rank("Leutnant", "Leutnant", WINGMAN_PRIVILEGE),
    rank("Oberleutnant", "Oberlt", LEADER_PRIVILEGE),
    rank("Rittmeister", "Rittm", COMMAND_PRIVILEGE),
  ],
};

/**
 * The manual gives Rittmeister and Hauptmann as equivalent titles for the top
 * German rank. This port promotes to Rittmeister and records the other, rather
 * than picking one and pretending the other does not exist.
 */
export const GERMAN_TOP_RANK_ALTERNATE = "Hauptmann";

/** The rank index that leads a flight, and the one that commands one. */
export const FLIGHT_LEADER_RANK = 1;
export const SQUADRON_COMMAND_RANK = 2;

export function rankOf(nation: Nation, index: number): Rank {
  const ladder = CAREER_RANKS[nation];
  const clamped = Math.min(Math.max(index, 0), ladder.length - 1);
  const found = ladder[clamped];
  if (!found) throw new Error(`no rank ${index} for ${nation}`);
  return found;
}

export function topRankIndex(nation: Nation): number {
  return CAREER_RANKS[nation].length - 1;
}

/** Career score needed for each rung. Port-side — see the module note. */
export const PROMOTION_SCORES: readonly number[] = [0, 1500, 5000];

export function rankForScore(score: number): number {
  let index = 0;
  for (const [rung, threshold] of PROMOTION_SCORES.entries()) {
    if (score >= threshold) index = rung;
  }
  return index;
}

/** Verbatim `PS.EXE` promotion message, `"You are promoted to %r."` filled in. */
export function promotionMessage(nation: Nation, index: number): string {
  return `You are promoted to ${rankOf(nation, index).title}.`;
}

export type FlightRole = "wingman" | "leader";

export function flightRoleOf(rankIndex: number): FlightRole {
  return rankIndex >= FLIGHT_LEADER_RANK ? "leader" : "wingman";
}

/**
 * How many aircraft the player's flight runs to at this rank: a wingman is one
 * of somebody else's pair, a flight leader has "only you and one other
 * fighter", and a Captain gets "command of flights of up to four aircraft"
 * (manual, "Rank promotions").
 */
export function flightSizeFor(rankIndex: number): number {
  if (rankIndex >= SQUADRON_COMMAND_RANK) return 4;
  if (rankIndex >= FLIGHT_LEADER_RANK) return 2;
  return 2;
}
